package com.banyan.flightviewer.web.components.util

import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * ISO8601 (or epoch-second string) -> 12-hour display time in the user's
 * local zone, e.g. "2:30 PM". Returns empty string on unparsable input so
 * callers can fall through to a dash.
 */
fun formatTime12Hour(iso: String): String {
    val instant = parseInstantOrNull(iso) ?: return ""
    val d = kotlin.js.Date(instant.toEpochMilliseconds())
    val hours = d.getHours()
    val minutes = d.getMinutes()
    val ampm = if (hours >= 12) "PM" else "AM"
    val h12 = ((hours + 11) % 12) + 1
    val mm = minutes.toString().padStart(2, '0')
    return "$h12:$mm $ampm"
}

/**
 * Current wall-clock time in the airport's IANA timezone, formatted 12-hr.
 * Returns empty string if the timezone ID is unknown or blank. Reuses
 * kotlinx-datetime's IANA support.
 */
fun formatLocalTimeAtAirport(timezone: String): String {
    if (timezone.isBlank()) return ""
    val tz = runCatching { TimeZone.of(timezone) }.getOrNull() ?: return ""
    val local = Clock.System.now().toLocalDateTime(tz)
    val hours = local.hour
    val minutes = local.minute
    val ampm = if (hours >= 12) "PM" else "AM"
    val h12 = ((hours + 11) % 12) + 1
    val mm = minutes.toString().padStart(2, '0')
    return "$h12:$mm $ampm"
}

/**
 * Format a bookmark/transaction timestamp (epoch ms) as "M/d h:mm a" in
 * local time. Matches desktop's `formatTimestamp()` in
 * [BookmarksPanel.kt](desktop/src/main/kotlin/com/banyan/flightviewer/ui/BookmarksPanel.kt).
 */
fun formatBookmarkTimestamp(epochMs: Long): String {
    val d = kotlin.js.Date(epochMs.toDouble())
    val month = d.getMonth() + 1
    val day = d.getDate()
    val hours = d.getHours()
    val minutes = d.getMinutes()
    val ampm = if (hours >= 12) "PM" else "AM"
    val h12 = ((hours + 11) % 12) + 1
    val mm = minutes.toString().padStart(2, '0')
    return "$month/$day $h12:$mm $ampm"
}

fun parseInstantOrNull(s: String): Instant? {
    if (s.isBlank() || s.equals("null", true)) return null
    return try {
        Instant.parse(s)
    } catch (_: Throwable) {
        try {
            Instant.fromEpochSeconds(s.toLong())
        } catch (_: Throwable) {
            null
        }
    }
}
